import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: string
  error?: string
  leading?: ReactNode
  /**
   * Classes for the field as a whole — margins, width, grid placement.
   *
   * Spacing belongs here, not on `className`. `className` styles the `<input>` itself, so a
   * margin passed there separates the box from its own label and knocks the leading icon,
   * which is centred against the wrapper, off centre by half the margin.
   */
  wrapperClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, wrapperClassName, label, hint, error, leading, id, ...props }, ref) => {
    const generatedId = useId()
    const inputId = id ?? generatedId
    const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined

    return (
      <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-[var(--text-primary)]">
            {label}
          </label>
        )}

        <div className="relative">
          {leading && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">
              {leading}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              'surface-raised h-11 w-full rounded-lg border px-3 text-base outline-none',
              'placeholder:text-[var(--text-secondary)]',
              'focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/25',
              leading && 'pl-10',
              error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/25',
              className,
            )}
            {...props}
          />
        </div>

        {error ? (
          <p id={`${inputId}-error`} className="text-sm text-danger-500">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="text-sm text-[var(--text-secondary)]">
            {hint}
          </p>
        ) : null}
      </div>
    )
  },
)

Input.displayName = 'Input'
